import type { ReactNode } from "react";
import { Link, Navigate } from "react-router";
import clsx from "clsx";
import { FlexvLogo } from "@/components/ui/FlexvLogo";
import { LanguageMenu, ThemeMenu } from "@/components/layout/HeaderControls";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/providers/AuthProvider";

/** Redirect sesi aktif: belum verifikasi -> OTP, sudah -> /plan. */
export function AuthAlreadyRedirect() {
    const { coach, isAuthenticated, initializing } = useAuth();
    if (initializing || !isAuthenticated) return null;
    if (coach && !coach.emailVerifiedAt) {
        return <Navigate to="/verify-email" replace state={{ email: coach.email ?? undefined }} />;
    }
    return <Navigate to="/plan" replace />;
}

/** Shared service strip — same gallery as Portal homepage (`home/textservice-*.webp`). */
const SERVICE_IMAGES = [
    "textservice-membership.webp",
    "textservice-attendance.webp",
    "textservice-class-schedule.webp",
    "textservice-coach.webp",
    "textservice-venuebooking.webp",
    "textservice-company-workspace.webp",
    "textservice-cashier.webp",
    "textservice-wallet.webp",
] as const;

export function AuthCtaButton({ to, children }: { to: string; children: ReactNode }) {
    return (
        <Link
            to={to}
            className="inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[var(--color-primary)] via-[#BF00FF] to-[#FFA600] px-8 text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/40"
        >
            {children}
        </Link>
    );
}

interface AuthLayoutProps {
    subtitle: string;
    heroCta: ReactNode;
    cardClass?: string;
    gridClass?: string;
    logoLink?: string;
    children: ReactNode;
}

/** Shared login/signup scaffold: hero kiri + card kanan + marquee bawah. */
export function AuthLayout({
    subtitle,
    heroCta,
    cardClass,
    gridClass,
    logoLink = "/login",
    children,
}: AuthLayoutProps) {
    const { t } = useI18n();
    return (
        <div className="app-page-scaffold flex min-h-screen flex-col overflow-x-hidden">
            <style>{`@keyframes auth-service-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}.auth-service-marquee-track{display:flex;width:max-content;animation:auth-service-marquee 40s linear infinite}.auth-service-marquee:hover .auth-service-marquee-track{animation-play-state:paused}@keyframes auth-gradient-slide{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}.auth-gradient-line{background-size:200% auto;animation:auth-gradient-slide 3s ease infinite}@media(prefers-reduced-motion:reduce){.auth-service-marquee-track,.auth-gradient-line{animation:none}.auth-service-marquee{overflow-x:auto}}`}</style>
            <div className="fixed left-4 top-4 z-30 lg:left-12 xl:left-16">
                <Link to={logoLink} aria-label="Flexv">
                    <FlexvLogo title="Flexv" className="app-text-primary h-9 w-auto" />
                </Link>
            </div>
            <div className="fixed right-4 top-4 z-30 flex items-center gap-2">
                <LanguageMenu />
                <ThemeMenu />
            </div>

            <div className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-center px-6 pb-8 pt-20 lg:px-12 xl:px-16">
                <div
                    className={clsx(
                        "grid w-full items-center gap-8 lg:gap-10 xl:gap-14",
                        gridClass ?? "lg:grid-cols-[1.05fr_0.95fr]",
                    )}
                >
                    <div className="min-w-0">
                        <p className="app-text-soft mt-6 text-sm font-bold uppercase tracking-[0.22em]">
                            {t("auth.heroEyebrow")}
                        </p>
                        <h1 className="app-text-primary mt-3 text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl xl:text-7xl">
                            {t("auth.heroTitleA")}
                            <br />
                            <span className="bg-gradient-to-r from-[#FFA600] via-[#ffd26d] to-[#BF00FF] bg-clip-text text-transparent">
                                {t("auth.heroTitleB")}
                            </span>
                        </h1>
                        <p className="app-shell-text-muted mt-5 max-w-xl text-lg leading-relaxed sm:text-xl">
                            {t("auth.heroDesc")}
                        </p>

                        <div
                            aria-hidden="true"
                            className="auth-gradient-line mt-8 h-[2px] w-full max-w-xl rounded-full bg-gradient-to-r from-[var(--color-primary)] via-[#BF00FF] to-[#FFA600]"
                        />
                        <div className="mt-6 flex flex-wrap items-center gap-4">{heroCta}</div>
                    </div>

                    <div
                        className={clsx(
                            "w-full justify-self-center lg:justify-self-end",
                            cardClass ?? "max-w-md",
                        )}
                    >
                        <div className="mb-6 text-center lg:text-left">
                            <h2 className="app-text-primary text-2xl font-extrabold">
                                {t("app.name")}
                            </h2>
                            <p className="app-shell-text-muted mt-1 text-sm">{subtitle}</p>
                        </div>
                        {children}
                    </div>
                </div>
            </div>

            <div
                className="auth-service-marquee mt-auto w-full overflow-hidden border-t border-black/5 pb-6 pt-4 [mask-image:linear-gradient(90deg,transparent,#000_7%,#000_93%,transparent)]"
                aria-hidden="true"
            >
                <div className="auth-service-marquee-track gap-4 pr-4">
                    {[...SERVICE_IMAGES, ...SERVICE_IMAGES].map((file, index) => (
                        <img
                            key={`${file}-${index}`}
                            src={`${import.meta.env.BASE_URL}${file}`}
                            alt=""
                            loading="lazy"
                            draggable={false}
                            className="h-24 w-44 shrink-0 select-none rounded-2xl border border-black/10 object-cover shadow-md sm:h-28 sm:w-52"
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
